/**
 * Le Philtre — limite de quantité par produit.
 *
 * La limite et son message sont propres à chaque produit : metafields
 * `custom.quantite_max` (entier) et `custom.message_quantite_max` (texte), exposés en
 * data-attributs par `snippets/quantity-max.liquid` sur :
 *   - <quantity-selector data-lp-max data-lp-max-message>  (fiche produit, panier, tiroir)
 *   - <product-form data-lp-max data-lp-max-message data-lp-product-id>  (ajout au panier)
 *
 * Trois garde-fous, tous en amont des écouteurs de Prestige (phase de capture) :
 *   1. bouton « + » : bloqué au-delà de la limite, message affiché ;
 *   2. saisie directe : ramenée à la limite, message affiché ;
 *   3. ajout au panier : bloqué si la limite est dépassée en tenant compte des
 *      quantités déjà au panier (toutes déclinaisons du produit confondues).
 * Au chargement, une ligne de panier déjà au-dessus de la limite est signalée.
 *
 * Remarque : c'est une limite côté navigateur (comme les règles de quantité de Prestige).
 * Une limite infranchissable côté serveur demanderait une Shopify Function de validation.
 */
(() => {
  const ALERT_CLASS = 'lp-quantity-alert';
  const ALERT_DURATION = 6000;

  const errorIcon = () =>
    '<svg width="13" height="13" fill="none" viewBox="0 0 13 13" aria-hidden="true" focusable="false">' +
    '<circle cx="6.5" cy="6.5" r="6.5" fill="currentColor"/>' +
    '<path fill="#fff" d="M6.75 7.97a.387.387 0 0 1-.3-.12.606.606 0 0 1-.12-.34l-.3-3.82c-.02-.247.033-.443.16-.59.127-.153.313-.23.56-.23.24 0 .42.077.54.23.127.147.18.343.16.59l-.3 3.82a.522.522 0 0 1-.12.34.344.344 0 0 1-.28.12Zm0 2.08a.744.744 0 0 1-.55-.21.751.751 0 0 1-.2-.54c0-.213.067-.387.2-.52.14-.14.323-.21.55-.21.233 0 .413.07.54.21.133.133.2.307.2.52 0 .22-.067.4-.2.54-.127.14-.307.21-.54.21Z"/>' +
    '</svg>';

  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------------------------ *
   * État du panier (pour l'ajout au panier)
   * ------------------------------------------------------------------ */

  let cartItems = null; // null = pas encore connu

  const fetchCart = () =>
    fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        cartItems = (cart && cart.items) || [];
        return cartItems;
      })
      .catch(() => {
        cartItems = []; // on ne bloque pas la vente si le panier est illisible
        return cartItems;
      });

  document.documentElement.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && Array.isArray(cart.items)) {
      cartItems = cart.items;
    } else {
      fetchCart();
    }
  });

  document.documentElement.addEventListener('cart:refresh', () => fetchCart());

  const quantityInCart = (productId) => {
    if (!cartItems) return 0;
    return cartItems.reduce(
      (total, item) => (item.product_id === productId ? total + item.quantity : total),
      0
    );
  };

  /* ------------------------------------------------------------------ *
   * Message
   * ------------------------------------------------------------------ */

  // Le message vient du réglage de thème (richtext : lien, gras, italique) ou du
  // metafield texte du produit : on ne garde que ces balises-là, et des liens sûrs.
  const ALLOWED_TAGS = { A: true, STRONG: true, EM: true, B: true, I: true, BR: true };
  const UNWRAP_TAGS = { P: true, DIV: true, SPAN: true };

  const sanitizeInto = (target, source) => {
    source.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        target.appendChild(document.createTextNode(node.textContent));
      } else if (node.nodeType === Node.ELEMENT_NODE && ALLOWED_TAGS[node.tagName]) {
        const element = document.createElement(node.tagName.toLowerCase());
        if (node.tagName === 'A') {
          const href = node.getAttribute('href') || '';
          if (/^(\/|https?:\/\/|mailto:)/i.test(href)) element.setAttribute('href', href);
        }
        sanitizeInto(element, node);
        target.appendChild(element);
      } else if (node.nodeType === Node.ELEMENT_NODE && UNWRAP_TAGS[node.tagName]) {
        if (target.childNodes.length) target.appendChild(document.createTextNode(' '));
        sanitizeInto(target, node);
      }
    });
  };

  const messageFragment = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const fragment = document.createDocumentFragment();
    sanitizeInto(fragment, template.content);
    return fragment;
  };

  const messageHtml = (html) => {
    const holder = document.createElement('div');
    holder.appendChild(messageFragment(html));
    return holder.innerHTML;
  };

  /** persist : constat durable (ligne de panier au-dessus de la limite), pas une réaction à un geste. */
  const showAlert = (anchor, message, { persist = false } = {}) => {
    if (!message) return;

    const host = anchor.closest('line-item-quantity') || anchor;
    const existing = host.nextElementSibling;
    if (existing && existing.classList.contains(ALERT_CLASS)) {
      if (existing.dataset.persist === 'true') return;
      existing.remove();
    }

    const alert = document.createElement('p');
    alert.className = `${ALERT_CLASS} h-stack gap-2 text-xs`;
    alert.setAttribute('role', 'alert');
    alert.innerHTML = `${errorIcon()}<span></span>`;
    alert.querySelector('span').appendChild(messageFragment(message));

    host.insertAdjacentElement('afterend', alert);

    if (persist) {
      alert.dataset.persist = 'true';
    } else {
      setTimeout(() => alert.remove(), ALERT_DURATION);
    }
  };

  /**
   * Fiche produit et achat rapide : bannière d'erreur native de Prestige (buy-buttons).
   * Cartes produit de la boutique : pas de bannière disponible, on pose notre message
   * sous le bouton.
   */
  const showFormError = (form, holder, message) => {
    if (!message) return;

    if (form.querySelector('buy-buttons')) {
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: messageHtml(message) } }));
    } else {
      showAlert(holder, message);
    }
  };

  /* ------------------------------------------------------------------ *
   * Limite d'un sélecteur de quantité
   * ------------------------------------------------------------------ */

  const limitOf = (element) => {
    const selector = element.closest('quantity-selector[data-lp-max]');
    if (!selector) return null;

    const max = parseInt(selector.dataset.lpMax, 10);
    if (!max || max <= 0) return null;

    return { selector, max, message: selector.dataset.lpMaxMessage || '' };
  };

  // 1. Bouton « + » (bouton sur la fiche produit, lien /cart/change dans le panier)
  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest && event.target.closest('[data-lp-role="increase"]');
      if (!control) return;

      const limit = limitOf(control);
      if (!limit) return;

      const input = limit.selector.querySelector('input');
      const step = parseInt(input.step, 10) || 1;
      const current = parseInt(input.value, 10) || 0;

      if (current + step > limit.max) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showAlert(limit.selector, limit.message);
      }
    },
    true
  );

  // 2. Saisie directe : on ramène à la limite avant que Prestige ne lise la valeur
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'number') return;

      const limit = limitOf(input);
      if (!limit) return;

      const value = parseInt(input.value, 10);
      if (Number.isNaN(value) || value <= limit.max) return;

      input.value = limit.max;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      showAlert(limit.selector, limit.message);
    },
    true
  );

  // 3. Ajout au panier : limite appliquée au total du panier pour ce produit
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.action || !form.action.includes('/cart/add')) return;

      const holder = form.closest('product-form[data-lp-max]');
      if (!holder) return;

      const max = parseInt(holder.dataset.lpMax, 10);
      const productId = parseInt(holder.dataset.lpProductId, 10);
      if (!max || max <= 0 || !productId) return;

      // Panier pas encore connu : on suspend l'envoi, on le lit, puis on renvoie le formulaire
      if (cartItems === null) {
        const submitter = event.submitter;
        event.preventDefault();
        event.stopImmediatePropagation();
        fetchCart().then(() => {
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
          } else {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        });
        return;
      }

      const quantityInput = form.querySelector('input[name="quantity"]');
      const requested = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
      const remaining = Math.max(0, max - quantityInCart(productId));

      if (requested > remaining) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showFormError(form, holder, holder.dataset.lpMaxMessage || '');
      }
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * Panier déjà au-dessus de la limite (limite posée après coup, lien /cart/add…)
   * ------------------------------------------------------------------ */

  const flagOverLimitLines = () => {
    document.querySelectorAll('quantity-selector[data-lp-max]').forEach((selector) => {
      const input = selector.querySelector('input[data-line-key]');
      if (!input) return;

      const max = parseInt(selector.dataset.lpMax, 10);
      const value = parseInt(input.value, 10);
      if (!max || Number.isNaN(value) || value <= max) return;

      showAlert(selector, selector.dataset.lpMaxMessage || '', { persist: true });
    });
  };

  const primeCart = () => {
    if (cartItems === null && document.querySelector('product-form[data-lp-max]')) fetchCart();
  };

  if (document.readyState !== 'loading') {
    primeCart();
    flagOverLimitLines();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      primeCart();
      flagOverLimitLines();
    });
  }
})();
